import type { BlogPostDraft } from '../../lib/blog';

export const post: BlogPostDraft = {
  slug: 'email-prospection-ugc',
  lang: 'fr',
  title: 'Email de prospection UGC : 7 exemples pour contacter les marques',
  description:
    'Découvre sept exemples d\'emails de prospection UGC à adapter pour contacter des marques, présenter ton portfolio et relancer tes prospects.',
  date: '2026-07-19',
  author: 'Équipe Leadsme',
  tags: ['prospection', 'email', 'ugc', 'exemples'],
  ctaTitleKey: 'blog.postCtaTitle',
  ctaDescriptionKey: 'blog.postCtaDescription',
  ctaButtonKey: 'blog.postCtaButton',
  content: `
<p class="blog-lead">
  La prospection fait partie du quotidien de beaucoup de créateurs UGC. Pourtant, au moment d'écrire à une marque, on finit souvent devant le même problème : que dire sans paraître trop insistant, trop générique ou trop commercial ?
</p>
<p>
  Il n'existe pas d'email magique qui garantit une réponse. En revanche, certains messages sont plus faciles à lire et donnent davantage envie de poursuivre la discussion.
</p>
<p>
  Le principe reste simple : montrer que tu ne contactes pas la marque au hasard, expliquer rapidement ce que tu peux lui apporter et permettre à ton interlocuteur de découvrir ton travail sans chercher pendant cinq minutes.
</p>
<p>
  Les exemples présentés dans cet article sont des <strong>modèles à personnaliser</strong>, et non des messages à copier-coller tels quels. Ils sont là pour t'aider à structurer ton email, mais quelques phrases doivent toujours être adaptées à la marque, à son univers et au contenu que tu souhaites lui proposer.
</p>
<p>
  Tu ne sais pas encore quelles entreprises prospecter ? Découvre nos conseils pour <a href="/blog/trouver-marques-ugc">trouver des marques à contacter en UGC</a>.
</p>
<p>
  Voici sept exemples à adapter selon ta situation.
</p>

<h2>1. L'email de prise de contact simple</h2>
<blockquote>
  <p><strong>Objet :</strong> Une idée de contenu pour [Marque]</p>
  <p>
    Bonjour [Prénom],<br/><br/>
    Je me permets de vous contacter car je suis [Marque] depuis quelque temps, notamment votre communication autour de [produit ou campagne].<br/><br/>
    Je suis créateur·rice UGC spécialisé·e dans [univers] et je pense que mon profil pourrait correspondre à votre manière de communiquer.<br/><br/>
    Voici mon portfolio : [lien]<br/><br/>
    Seriez-vous disponible pour échanger sur vos prochains besoins en contenu ?<br/><br/>
    Bonne journée,<br/>
    [Prénom]
  </p>
</blockquote>
<p>
  Ce modèle fonctionne bien pour un premier contact. Il reste direct, sans essayer de tout expliquer dans un seul email.
</p>
<p>
  Le détail important se trouve dans la première phrase. Évite les compliments vagues comme « J'adore votre marque ». Mentionne plutôt un produit, une campagne ou un élément précis de leur communication.
</p>

<h2>2. Quand tu as déjà une idée de vidéo</h2>
<blockquote>
  <p><strong>Objet :</strong> Une idée de vidéo pour [Produit]</p>
  <p>
    Bonjour [Prénom],<br/><br/>
    En découvrant [produit], j'ai imaginé un format UGC autour de [décrire l'idée en une phrase].<br/><br/>
    L'idée serait de montrer [bénéfice, usage ou situation] dans une vidéo courte et naturelle, pensée pour [TikTok, Instagram Reels ou publicité].<br/><br/>
    Je serais ravi·e de vous détailler le concept si vous recherchez actuellement de nouveaux contenus.<br/><br/>
    Voici quelques exemples de mon travail : [lien]<br/><br/>
    Bonne journée,<br/>
    [Prénom]
  </p>
</blockquote>
<p>
  Tu n'as pas besoin d'envoyer un script complet dès le premier message. Une idée suffisamment concrète montre déjà que tu t'es intéressé·e à la marque.
</p>

<h2>3. Quand tu utilises réellement le produit</h2>
<blockquote>
  <p><strong>Objet :</strong> Création de contenu autour de [Produit]</p>
  <p>
    Bonjour [Prénom],<br/><br/>
    J'utilise [produit] depuis [durée ou contexte] et j'apprécie particulièrement [élément précis et sincère].<br/><br/>
    Comme je travaille également en tant que créateur·rice UGC, je me suis dit qu'il pourrait être intéressant de créer du contenu autour de mon utilisation réelle du produit.<br/><br/>
    Je réalise notamment des vidéos de type [témoignage, démonstration, routine, unboxing…].<br/><br/>
    Voici mon portfolio : [lien]<br/><br/>
    Est-ce un format que vous recherchez actuellement ?<br/><br/>
    Bonne journée,<br/>
    [Prénom]
  </p>
</blockquote>
<p>
  Cette approche est intéressante uniquement lorsque tu connais vraiment le produit. Inutile de prétendre être client·e d'une marque simplement pour faciliter la prise de contact.
</p>

<h2>4. Quand tu as déjà travaillé dans le même secteur</h2>
<blockquote>
  <p><strong>Objet :</strong> Contenu UGC pour [Marque]</p>
  <p>
    Bonjour [Prénom],<br/><br/>
    J'ai récemment réalisé du contenu pour [marque ou type de marque], dans l'univers de [secteur].<br/><br/>
    Je pense que certains formats pourraient également être pertinents pour [Marque], notamment autour de [produit ou usage].<br/><br/>
    Vous pouvez retrouver le projet ici : [lien direct vers la vidéo]<br/><br/>
    Et mon portfolio complet ici : [lien]<br/><br/>
    Seriez-vous la bonne personne pour échanger à ce sujet ?<br/><br/>
    Bonne journée,<br/>
    [Prénom]
  </p>
</blockquote>
<p>
  Lorsque tu mentionnes une précédente collaboration, choisis un exemple vraiment pertinent. Une vidéo dans le même secteur sera souvent plus convaincante qu'une longue liste de marques sans rapport.
</p>

<h2>5. L'email très court</h2>
<blockquote>
  <p><strong>Objet :</strong> Création UGC pour [Marque]</p>
  <p>
    Bonjour [Prénom],<br/><br/>
    Je suis créateur·rice UGC dans l'univers de [secteur] et j'aimerais vous proposer mes services pour vos prochains contenus.<br/><br/>
    Voici mon portfolio : [lien]<br/><br/>
    Avez-vous actuellement des besoins en création UGC ?<br/><br/>
    Bonne journée,<br/>
    [Prénom]
  </p>
</blockquote>
<p>
  Ce format peut être utile lorsque tu contactes beaucoup de marques ou lorsque tu n'as pas encore trouvé d'angle très précis.
</p>
<p>
  Il vaut néanmoins mieux ajouter une ligne personnalisée dès que possible. Quelques mots suffisent pour montrer que l'email n'a pas été envoyé automatiquement à cinquante entreprises.
</p>

<h2>6. La relance après un premier email</h2>
<blockquote>
  <p><strong>Objet :</strong> Re: Une idée de contenu pour [Marque]</p>
  <p>
    Bonjour [Prénom],<br/><br/>
    Je me permets de revenir vers vous concernant mon précédent message.<br/><br/>
    Je serais toujours ravi·e d'échanger sur vos éventuels besoins en contenu UGC. Je vous remets mon portfolio ici pour plus de simplicité : [lien]<br/><br/>
    N'hésitez pas à me dire si le sujet n'est pas d'actualité pour le moment.<br/><br/>
    Bonne journée,<br/>
    [Prénom]
  </p>
</blockquote>
<p>
  Une absence de réponse ne signifie pas forcément que la marque n'est pas intéressée. Ton message a peut-être été lu au mauvais moment ou simplement oublié.
</p>
<p>
  Tu peux envoyer une première relance environ cinq à sept jours après ton email initial. Il n'est pas nécessaire de réécrire toute ta proposition. Pour aller plus loin : <a href="/blog/relancer-marque-ugc">comment relancer une marque en UGC sans paraître insistant·e</a>.
</p>

<h2>7. Le message LinkedIn ou Instagram</h2>
<blockquote>
  <p>
    Bonjour [Prénom],<br/><br/>
    Je me permets de vous écrire car je vous ai envoyé un email il y a quelques jours au sujet d'une proposition de contenu UGC pour [Marque].<br/><br/>
    Je vous laisse mon portfolio ici au cas où : [lien]<br/><br/>
    Bonne journée !
  </p>
</blockquote>
<p>
  Ce message doit rester court. L'objectif n'est pas de recopier ton email dans les messages privés, mais simplement de t'assurer qu'il est arrivé à la bonne personne.
</p>
<p>
  Évite également de multiplier les relances sur tous les canaux le même jour.
</p>

<h2>Ce qu'il faut vérifier avant d'envoyer ton email</h2>
<p>
  Relis toujours le nom de la marque et celui de ton interlocuteur. Les erreurs de copier-coller arrivent vite lorsque tu envoies plusieurs messages à la suite.
</p>
<p>
  Vérifie aussi que ton portfolio s'ouvre correctement, notamment sur mobile. Ton interlocuteur doit pouvoir comprendre rapidement ce que tu proposes et regarder quelques vidéos sans créer de compte ni demander un accès.
</p>
<p>
  Enfin, termine ton email par une question simple. Par exemple :
</p>
<ul>
  <li>Avez-vous actuellement des besoins en contenu UGC ?</li>
  <li>Êtes-vous la bonne personne pour en discuter ?</li>
  <li>Seriez-vous disponible pour un échange rapide ?</li>
  <li>Puis-je vous envoyer quelques idées de concepts ?</li>
</ul>
<p>
  Une question précise est plus facile à traiter qu'un vague « N'hésitez pas à revenir vers moi ».
</p>

<h2>Pour résumer</h2>
<p>
  Un bon email de prospection UGC n'a pas besoin d'être long ou particulièrement original. Il doit surtout être personnalisé, facile à comprendre et accompagné d'un portfolio accessible.
</p>
<p>
  Les modèles présentés dans cet article servent uniquement de point de départ. Copier exactement le même message ne garantit pas d'obtenir une réponse : le plus important reste d'adapter quelques lignes à chaque marque, de montrer que tu connais son univers et de lui proposer une approche cohérente avec ses besoins.
</p>
`,
};
